import React from 'react';
import { Palette } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';

const DRAWING_COLORS = [
  '#FFFFFF', '#F3F4F6', '#E5E7EB', '#D1D5DB',
  '#9CA3AF', '#6B7280', '#374151', '#1F2937',
  '#000000', '#EF4444', '#F97316', '#F59E0B',
  '#EAB308', '#84CC16', '#22C55E', '#10B981',
  '#06B6D4', '#3B82F6', '#6366F1', '#EC4899'
];

export default function DrawingPropertiesPanel({ 
  element, 
  onUpdate,
  position 
}) {
  if (!element || element.type !== 'drawing') return null;
  
  const props = element.properties || {};
  const currentColor = props.stroke || '#000000';
  const currentStrokeWidth = props.strokeWidth || 2;

  const handleColorChange = (color) => {
    onUpdate(element.id, {
      properties: { ...element.properties, stroke: color }
    });
  };

  const handleStrokeWidthChange = (width) => {
    onUpdate(element.id, {
      properties: { ...element.properties, strokeWidth: width }
    });
  };

  return (
    <div 
      className="absolute z-50 flex items-center gap-1 bg-background rounded-lg border shadow-lg p-1"
      style={{ 
        left: position?.x || 100, 
        top: Math.max(10, (position?.y || 100) - 50)
      }}
    >
      {/* Color Picker */}
      <Popover>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 px-2 gap-2"
            data-testid="drawing-color-picker"
          >
            <div 
              className="w-4 h-4 rounded-full border border-gray-300"
              style={{ backgroundColor: currentColor }}
            />
            <Palette className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" side="bottom">
          <p className="text-xs font-medium mb-2">Line Color</p>
          <div className="grid grid-cols-5 gap-1">
            {DRAWING_COLORS.map(color => (
              <button
                key={color}
                className={`w-6 h-6 rounded-full border-2 transition-all ${
                  currentColor === color 
                    ? 'border-primary scale-110' 
                    : 'border-transparent hover:border-gray-400'
                }`}
                style={{ backgroundColor: color }}
                onClick={() => handleColorChange(color)}
                data-testid={`drawing-color-${color.replace('#', '')}`}
              />
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {/* Stroke Width */}
      <Popover>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 px-2 gap-1"
            data-testid="drawing-stroke-picker"
          >
            <div className="flex items-center gap-1">
              <div 
                className="w-6 rounded-full"
                style={{ 
                  height: Math.min(currentStrokeWidth, 6), 
                  backgroundColor: currentColor,
                  minHeight: 2
                }}
              />
              <span className="text-xs">{currentStrokeWidth}px</span>
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-3" side="bottom">
          <p className="text-xs font-medium mb-2">Line Width: {currentStrokeWidth}px</p>
          <Slider
            value={[currentStrokeWidth]}
            onValueChange={([val]) => handleStrokeWidthChange(val)}
            min={1}
            max={20}
            step={1}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
